import type { AdminInvoiceRow } from '../../../domain/superadmin/types';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { cn } from '@/lib/utils';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../components/ui/table';

interface InvoicesTableProps {
    items: AdminInvoiceRow[];
}

type ColumnKey =
    | 'organizationName'
    | 'status'
    | 'amount'
    | 'paidAt'
    | 'link'
    | 'number';

const COLUMNS: { key: ColumnKey; label: string; className?: string }[] = [
    { key: 'organizationName', label: 'Empresa' },
    { key: 'status', label: 'Status' },
    { key: 'amount', label: 'Valor', className: 'text-right' },
    { key: 'paidAt', label: 'Pago em' },
    { key: 'link', label: 'Links' },
    { key: 'number', label: 'Número' },
];

const formatLabel = (value: string | null) => {
    if (!value) return '—';
    return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

function invoiceStatusClass(status: string) {
    const normalized = status.toLowerCase();
    if (normalized === 'paid') return 'border-success/30 bg-success-bg text-success-text';
    if (normalized === 'open' || normalized === 'draft') {
        return 'border-warning/30 bg-warning-bg text-warning-text';
    }
    return 'border-border bg-surface-subtle text-secondary';
}

function renderLinks(row: AdminInvoiceRow) {
    if (!row.invoicePdf && !row.hostedInvoiceUrl) {
        return <span className="text-muted">—</span>;
    }

    return (
        <div className="flex flex-wrap gap-2">
            {row.hostedInvoiceUrl && (
                <a
                    href={row.hostedInvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                >
                    Abrir
                </a>
            )}
            {row.invoicePdf && (
                <a
                    href={row.invoicePdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                >
                    PDF
                </a>
            )}
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    return (
        <span
            className={cn(
                'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                invoiceStatusClass(status),
            )}
        >
            {formatLabel(status)}
        </span>
    );
}

function renderCell(row: AdminInvoiceRow, key: ColumnKey) {
    if (key === 'organizationName') {
        return <span className="font-medium text-default">{row.organizationName}</span>;
    }
    if (key === 'status') {
        return <StatusBadge status={row.status} />;
    }
    if (key === 'amount') {
        return (
            <span className="font-mono tabular-nums">
                {formatCurrency(row.amountPaidCents / 100, row.currency || 'BRL')}
            </span>
        );
    }
    if (key === 'paidAt') {
        return <span className="text-muted">{row.paidAt ? formatDate(row.paidAt) : '—'}</span>;
    }
    if (key === 'link') {
        return renderLinks(row);
    }
    if (key === 'number') {
        return (
            <span className="font-mono text-xs text-muted">{row.number ? `#${row.number}` : '—'}</span>
        );
    }
    return null;
}

export function InvoicesTable({ items }: InvoicesTableProps) {
    if (items.length === 0) {
        return (
            <p className="py-12 text-center text-sm text-muted">
                Nenhuma fatura encontrada.
            </p>
        );
    }

    return (
        <>
            <ul className="m-0 flex list-none flex-col gap-3 p-0 md:hidden">
                {items.map((row) => (
                    <li
                        key={row.id}
                        className="rounded-xl border border-subtle bg-card p-4"
                    >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="min-w-0">
                                <p className="font-medium text-default">{row.organizationName}</p>
                                {row.number && (
                                    <p className="mt-0.5 font-mono text-[11px] text-muted">
                                        #{row.number}
                                    </p>
                                )}
                            </div>
                            <StatusBadge status={row.status} />
                        </div>
                        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted">
                            <dt>Valor</dt>
                            <dd className="text-right font-mono tabular-nums text-secondary">
                                {formatCurrency(row.amountPaidCents / 100, row.currency || 'BRL')}
                            </dd>
                            <dt>Pago em</dt>
                            <dd className="text-right text-secondary">
                                {row.paidAt ? formatDate(row.paidAt) : '—'}
                            </dd>
                        </dl>
                        <div className="mt-3">{renderLinks(row)}</div>
                    </li>
                ))}
            </ul>

            <div className="hidden md:block">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {COLUMNS.map((col) => (
                                <TableHead key={col.key} className={col.className}>
                                    {col.label}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((row) => (
                            <TableRow key={row.id}>
                                {COLUMNS.map((col) => (
                                    <TableCell key={col.key} className={col.className}>
                                        {renderCell(row, col.key)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </>
    );
}
